import {initContract} from '@ts-rest/core';
import {DB_SCHEMAS} from '../db/schemaExports';
import {z} from 'zod';
import {paginationQuerySchema, paginatedResponseSchema} from '../utils/pagination';

const c = initContract();

// Per-assistant enabled-tool entries. `toolId` references a tool from the
// catalog (built-in or, in a future phase, an external MCP server). `config`
// is opaque to the contract so individual tools can carry their own settings
// (e.g. an API key reference, a default max-results value).
export const assistantEnabledToolSchema = z.object({
  toolId: z.string().min(1).max(200),
  config: z.record(z.string(), z.unknown()).optional()
});
export type AssistantEnabledToolInput = z.infer<typeof assistantEnabledToolSchema>;

// drizzle-zod emits `unknown` (or a generic JSON shape) for jsonb columns
// even with `$type<...>` overrides, so we override the typed enabledTools
// shape on every response that surfaces the assistant row.
const assistantSelectShape = DB_SCHEMAS.selectAssistant.extend({
  enabledTools: z.array(assistantEnabledToolSchema)
});
const assistantWithKbsShape = assistantSelectShape.extend({
  knowledgeBaseIds: z.array(z.uuid())
});

export const assistantsContract = c.router(
  {
    create: {
      method: 'POST',
      path: '/',
      responses: {
        201: assistantWithKbsShape,
        403: z.object({message: z.string()})
      },
      body: DB_SCHEMAS.createAssistant
        .pick({
          name: true,
          description: true,
          prompt: true,
          userInstructions: true,
          firstMessage: true,
          avatar: true,
          tenantId: true
        })
        .extend({
          description: z.string().max(300).nullish(),
          prompt: z.string().max(100000),
          userInstructions: z.string().max(5000).nullish(),
          firstMessage: z.string().max(5000).nullish(),
          knowledgeBaseIds: z.array(z.uuid()).optional(),
          enabledTools: z.array(assistantEnabledToolSchema).optional()
        }),
      summary: 'Create an assistant'
    },
    get: {
      method: 'GET',
      path: '/:id',
      pathParams: z.object({
        id: z.string()
      }),
      responses: {
        200: assistantWithKbsShape,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      }
    },
    update: {
      method: 'PUT',
      path: '/:id',
      pathParams: z.object({
        id: z.string()
      }),
      body: DB_SCHEMAS.updateAssistant
        .pick({
          name: true,
          description: true,
          prompt: true,
          userInstructions: true,
          firstMessage: true,
          avatar: true
        })
        .extend({
          description: z.string().max(300).nullish(),
          prompt: z.string().max(100000).optional(),
          userInstructions: z.string().max(5000).nullish(),
          firstMessage: z.string().max(5000).nullish(),
          knowledgeBaseIds: z.array(z.uuid()).optional(),
          enabledTools: z.array(assistantEnabledToolSchema).optional()
        }),
      responses: {
        200: assistantWithKbsShape,
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      }
    },
    list: {
      method: 'GET',
      path: `/`,
      query: z
        .object({
          tenantId: z.string()
        })
        .merge(paginationQuerySchema)
        .extend({
          sortBy: z.enum(['name', 'createdAt', 'updatedAt']).optional()
        }),
      responses: {
        200: paginatedResponseSchema(assistantSelectShape),
        403: z.object({message: z.string()})
      },
      summary: 'Get all assistants'
    },
    delete: {
      method: 'DELETE',
      path: '/:id',
      pathParams: z.object({
        id: z.string()
      }),
      responses: {
        200: z.array(assistantSelectShape),
        403: z.object({message: z.string()}),
        404: z.object({message: z.string()})
      },
      body: null
    }
  },
  {
    pathPrefix: '/assistants'
  }
);
